import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogOverlay, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { 
  X,
  Expand,
  Copy,
  Share,
  MoreHorizontal,
  MessageCircle,
  Paperclip,
  Clock,
  ThumbsUp,
  Bot,
  Send,
  Plus,
  Upload,
  User,
  UserPlus,
  Users,
  Calendar as CalendarIcon,
  Tag,
  AlertCircle,
  FileText,
  Link2,
  Eye,
  EyeOff,
  History,
  Target,
  Activity,
  Loader2,
  ArrowDown,
  Check,
  Edit3,
  Zap,
  TrendingUp,
  FileUp
} from "lucide-react";
import { Task2, Project } from "@/types/tasks";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { useAutoSave } from "@/hooks/useAutoSave";
import { listMembros, getMembroById, getIniciais } from "@/services/profilesCache";
import { getCurrentUserId, getCurrentUserName } from "@/utils/userUtils";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

interface TaskDetailsModalProps {
  task: Task2;
  projects: Project[];
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (task: Task2) => void;
  isFullscreen?: boolean;
  onToggleFullscreen: () => void;
}

const statusOrder = ['not_started', 'in_progress', 'waiting', 'completed'];
const statusLabels = {
  'not_started': 'Não Iniciado',
  'in_progress': 'Em Execução', 
  'waiting': 'Aguardando',
  'completed': 'Concluído'
};

// Cores personalizadas para disciplinas
const disciplinaColors = {
  'Elétrica': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Hidráulica': 'bg-blue-100 text-blue-800 border-blue-200',
  'Estrutura': 'bg-gray-100 text-gray-800 border-gray-200',
  'Arquitetura': 'bg-purple-100 text-purple-800 border-purple-200',
  'Acabamento': 'bg-green-100 text-green-800 border-green-200',
  'Civil': 'bg-orange-100 text-orange-800 border-orange-200',
  'Qualidade': 'bg-red-100 text-red-800 border-red-200',
  'Geral': 'bg-slate-100 text-slate-800 border-slate-200'
};

export function TaskDetailsModal({ 
  task, 
  projects, 
  isOpen, 
  onClose, 
  onUpdate,
  isFullscreen = false,
  onToggleFullscreen
}: TaskDetailsModalProps) {
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [newSubtask, setNewSubtask] = useState("");
  const [isPrivateComment, setIsPrivateComment] = useState(false);
  const [showAssigneeSelect, setShowAssigneeSelect] = useState(false);
  const [buscaMembro, setBuscaMembro] = useState("");
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [showSetorSelect, setShowSetorSelect] = useState(false);
  const [showTagsMenu, setShowTagsMenu] = useState(false);
  // Inicializar com "Projeto/Obra" por padrão para novas tarefas
  const [activeTags, setActiveTags] = useState<string[]>(() => {
    // Se é uma nova tarefa (ID temporário), adicionar "Projeto/Obra" automaticamente
    const isNewTask = task.id.startsWith('temp_') || task.id.startsWith('task_') && task.createdAt && 
      (Date.now() - task.createdAt.getTime()) < 60000; // Menos de 1 minuto desde criação
    return isNewTask ? ["Projeto/Obra"] : [];
  });
  const [showSetorBlock, setShowSetorBlock] = useState(true);
  const [showClientSelect, setShowClientSelect] = useState(false);
  const [showSupplierSelect, setShowSupplierSelect] = useState(false);
  const [showMaterialSelect, setShowMaterialSelect] = useState(false);
  const [showEquipmentSelect, setShowEquipmentSelect] = useState(false);
  const [showProjectSelect, setShowProjectSelect] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showFollowersSelect, setShowFollowersSelect] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showSuccessFeedback, setShowSuccessFeedback] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const commentTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Smart auto-save with dirty checking and debounce
  const { data: editedTask, updateData, saveNow, state, getInlineStatus } = useAutoSave({
    initialData: task,
    onSave: async (taskData) => {
      try {
        onUpdate(taskData);
        return { 
          success: true, 
          updated: true,
          updatedAt: new Date() 
        };
      } catch (error) {
        return { success: false };
      }
    },
    debounceMs: 3000,
    toastThrottleMs: 30000
  });

  
  // Initialize progress state after calculateProgress is defined
  useEffect(() => {
    setNewProgress(calculateProgress());
  }, [editedTask.checklist]);

  const [newProgress, setNewProgress] = useState(0);

  // Delegação — responsável atual e membros disponíveis para escolher.
  const responsavel = editedTask.assignees[0];
  const membrosFiltrados = React.useMemo(() => {
    const termo = buscaMembro.trim().toLowerCase();
    return listMembros().filter((m) => !termo || m.nome.toLowerCase().includes(termo));
  }, [buscaMembro, showAssigneeSelect]);
  const membrosDisponiveis = React.useMemo(
    () =>
      listMembros()
        .filter((m) => !editedTask.followers.some((f) => f.id === m.id))
        .map((m) => ({ id: m.id, name: m.nome, avatar: m.avatarUrl ?? undefined })),
    [editedTask.followers, showFollowersSelect],
  );

  // Focus management
  useEffect(() => {
    if (isOpen && titleInputRef.current) {
      setTimeout(() => titleInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Enter para enviar atualização
      if (e.ctrlKey && e.key === 'Enter' && newComment.trim()) {
        e.preventDefault();
        handleAddComment();
        return;
      }
      
      if (e.key === 'Escape' && !state.isDirty) {
        onClose();
      } else if (e.key === 'Escape' && state.isDirty) {
        if (confirm('Você tem alterações não salvas. Deseja sair mesmo assim?')) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, state.isDirty, onClose, newComment]);

  const handleModalClose = () => {
    if (state.isDirty) {
      if (confirm('Você tem alterações não salvas. Deseja sair mesmo assim?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleStatusChange = (newStatus: string) => {
    updateData(prev => ({ 
      ...prev, 
      status: newStatus, 
      updatedAt: new Date(),
      comments: [...prev.comments, {
        id: Date.now().toString(),
        userId: "system",
        userName: "Sistema",
        message: `Status alterado para ${statusLabels[newStatus as keyof typeof statusLabels]}`,
        createdAt: new Date(),
        isPrivate: false
      }]
    }));
  };

  // Delegação: usa os membros reais (profiles) hidratados no login.
  const handleAssigneeChange = (userId: string) => {
    const membro = getMembroById(userId);
    if (membro) {
      const anterior = editedTask.assignees[0]?.name;
      updateData(prev => ({
        ...prev,
        assignees: [{ id: membro.id, name: membro.nome, avatar: membro.avatarUrl ?? undefined }],
        updatedAt: new Date(),
        comments: [...prev.comments, {
          id: Date.now().toString(),
          userId: "system",
          userName: "Sistema",
          message: anterior
            ? `Responsável alterado de ${anterior} para ${membro.nome}`
            : `${membro.nome} definido como responsável`,
          createdAt: new Date(),
          isPrivate: false,
        }],
      }));
    }
    setBuscaMembro("");
    setShowAssigneeSelect(false);
  };

  /** Remove o responsável, deixando a tarefa sem delegação. */
  const handleAssigneeClear = () => {
    const anterior = editedTask.assignees[0]?.name;
    updateData(prev => ({
      ...prev,
      assignees: [],
      updatedAt: new Date(),
      comments: anterior
        ? [...prev.comments, {
            id: Date.now().toString(),
            userId: "system",
            userName: "Sistema",
            message: `${anterior} removido como responsável`,
            createdAt: new Date(),
            isPrivate: false,
          }]
        : prev.comments,
    }));
    setBuscaMembro("");
    setShowAssigneeSelect(false);
  };

  const handleDateChange = (date: Date | undefined, type: 'start' | 'end') => {
    if (!date) return;
    
    if (type === 'start') {
      updateData(prev => ({ ...prev, startDate: date, updatedAt: new Date() }));
      setShowStartDatePicker(false);
    } else {
      updateData(prev => ({ ...prev, endDate: date, updatedAt: new Date() }));
      setShowEndDatePicker(false);
    }
  };

  const handleSetorChange = (setor: string) => {
    updateData(prev => ({ ...prev, setor, updatedAt: new Date() }));
    setShowSetorSelect(false);
  };

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    
    const comment = {
      id: Date.now().toString(),
      userId: getCurrentUserId() ?? "current-user",
      userName: getCurrentUserName(),
      message: newComment,
      createdAt: new Date(),
      isPrivate: isPrivateComment
    };
    
    updateData(prev => ({
      ...prev,
      comments: [...prev.comments, comment]
    }));
    setNewComment("");
    
    // Mostrar feedback de sucesso
    setShowSuccessFeedback(true);
    setTimeout(() => setShowSuccessFeedback(false), 2000);
  };

  const handleAddSubtask = () => {
    if (!newSubtask.trim()) return;
    
    const subtask = {
      id: Date.now().toString(),
      title: newSubtask,
      completed: false
    };
    
    updateData(prev => ({
      ...prev,
      checklist: [...prev.checklist, subtask]
    }));
    setNewSubtask("");
  };

  const toggleSubtask = (subtaskId: string) => {
    updateData(prev => ({
      ...prev,
      checklist: prev.checklist.map(item =>
        item.id === subtaskId ? { ...item, completed: !item.completed } : item
      )
    }));
  };

  const handleCopyTask = () => {
    const newTask = {
      ...editedTask,
      id: Date.now().toString(),
      title: `${editedTask.title} (Cópia)`,
      status: 'not_started',
      createdAt: new Date(),
      updatedAt: new Date(),
      comments: [{
        id: Date.now().toString(),
        userId: getCurrentUserId() ?? "current-user",
        userName: getCurrentUserName(),
        message: "Tarefa criada por cópia",
        createdAt: new Date(),
        isPrivate: false
      }]
    };
    
    onUpdate(newTask);
    alert('Tarefa copiada com sucesso!');
  };

  const handleExportTask = () => {
    const publicLink = `${window.location.origin}/tarefas/publico/${editedTask.id}`;
    navigator.clipboard.writeText(publicLink);
    alert('Link público copiado para a área de transferência!');
  };

  const handleAddTag = (tagType: string) => {
    if (!activeTags.includes(tagType)) {
      setActiveTags(prev => [...prev, tagType]);
    }
    setShowTagsMenu(false);
  };

  const handleRemoveTag = (tagType: string) => {
    setActiveTags(prev => prev.filter(tag => tag !== tagType));
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const maxSize = 100 * 1024 * 1024; // 100MB em bytes

    // Validar tamanho do arquivo
    if (file.size > maxSize) {
      alert('Arquivo muito grande! O limite é de 100MB.');
      return;
    }

    try {
      // Simular upload - em produção seria enviado para Supabase Storage
      const newAttachment = {
        id: Date.now().toString(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: URL.createObjectURL(file), // URL temporária para preview
        uploadedAt: new Date(),
        uploadedBy: getCurrentUserName()
      };

      // Adicionar arquivo à lista de anexos da tarefa
      updateData(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), newAttachment],
        updatedAt: new Date(),
        comments: [...prev.comments, {
          id: Date.now().toString(),
          userId: getCurrentUserId() ?? "current-user",
          userName: getCurrentUserName(),
          message: `Arquivo anexado: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`,
          createdAt: new Date(),
          isPrivate: false
        }]
      }));

      // Limpar input para permitir upload do mesmo arquivo novamente se necessário
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Erro no upload:', error);
      alert('Erro ao fazer upload do arquivo. Tente novamente.');
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleAddFollower = (userId: string) => {
    const membro = getMembroById(userId);
    const userToAdd = membro
      ? { id: membro.id, name: membro.nome, avatar: membro.avatarUrl ?? undefined }
      : undefined;

    if (userToAdd && !editedTask.followers.find(f => f.id === userId)) {
      updateData(prev => ({
        ...prev,
        followers: [...prev.followers, userToAdd],
        updatedAt: new Date(),
        comments: [...prev.comments, {
          id: Date.now().toString(),
          userId: "system",
          userName: "Sistema",
          message: `${userToAdd.name} adicionado como seguidor`,
          createdAt: new Date(),
          isPrivate: false
        }]
      }));
    }
    setShowFollowersSelect(false);
  };

  const handleRemoveFollower = (userId: string) => {
    const followerToRemove = editedTask.followers.find(f => f.id === userId);
    if (followerToRemove) {
      updateData(prev => ({
        ...prev,
        followers: prev.followers.filter(f => f.id !== userId),
        updatedAt: new Date(),
        comments: [...prev.comments, {
          id: Date.now().toString(),
          userId: "system",
          userName: "Sistema",
          message: `${followerToRemove.name} removido dos seguidores`,
          createdAt: new Date(),
          isPrivate: false
        }]
      }));
    }
  };

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only set dragging to false if we're leaving the main container
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSize = 100 * 1024 * 1024; // 100MB em bytes

      // Validar tamanho do arquivo
      if (file.size > maxSize) {
        alert(`Arquivo ${file.name} muito grande! O limite é de 100MB.`);
        continue;
      }

      try {
        // Simular upload - em produção seria enviado para Supabase Storage
        const newAttachment = {
          id: `${Date.now()}_${i}`,
          name: file.name,
          size: file.size,
          type: file.type,
          url: URL.createObjectURL(file), // URL temporária para preview
          uploadedAt: new Date(),
          uploadedBy: getCurrentUserName()
        };

        // Adicionar arquivo à lista de anexos da tarefa
        updateData(prev => ({
          ...prev,
          attachments: [...(prev.attachments || []), newAttachment],
          updatedAt: new Date(),
          comments: [...prev.comments, {
            id: `${Date.now()}_comment_${i}`,
            userId: getCurrentUserId() ?? "current-user",
            userName: getCurrentUserName(),
            message: `Arquivo anexado: ${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`,
            createdAt: new Date(),
            isPrivate: false
          }]
        }));

      } catch (error) {
        console.error('Erro no upload:', error);
        alert(`Erro ao fazer upload do arquivo ${file.name}. Tente novamente.`);
      }
    }
  };

  const handleReplyToComment = (commentId: string, userName: string) => {
    setReplyingTo(commentId);
    setNewComment(`@${userName} `);
    // Foca no input de comentário
    setTimeout(() => {
      if (commentTextareaRef.current) {
        commentTextareaRef.current.focus();
        commentTextareaRef.current.setSelectionRange(commentTextareaRef.current.value.length, commentTextareaRef.current.value.length);
      }
    }, 100);
  };

  const calculateProgress = () => {
    // If manual progress is set, use it instead of calculated
    if (editedTask.manualProgress !== undefined) {
      return editedTask.manualProgress;
    }
    
    if (editedTask.checklist.length === 0) return 0;
    const completed = editedTask.checklist.filter(item => item.completed).length;
    return Math.round((completed / editedTask.checklist.length) * 100);
  };

  const getProgressColor = (progress: number) => {
    if (progress === 100) return "text-green-600"; // Verde
    if (progress >= 60.1) return "text-green-400"; // Verde claro  
    if (progress >= 10.1) return "text-yellow-500"; // Amarelo
    return "text-red-500"; // Vermelho (0-10%)
  };

  const getProgressBgColor = (progress: number) => {
    if (progress === 100) return "bg-green-600"; // Verde
    if (progress >= 60.1) return "bg-green-400"; // Verde claro
    if (progress >= 10.1) return "bg-yellow-500"; // Amarelo  
    return "bg-red-500"; // Vermelho (0-10%)
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'not_started': return 'hsl(var(--muted))';
      case 'in_progress': return 'hsl(var(--primary))';
      case 'waiting': return 'hsl(var(--warning))';
      case 'completed': return 'hsl(var(--success))';
      default: return 'hsl(var(--muted))';
    }
  };

  const formatDateTime = (date: Date) => {
    return date.toLocaleString('pt-BR');
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('pt-BR');
  };

  const isOverdue = (date: Date) => {
    return date < new Date() && editedTask.status !== 'completed';
  };

  const currentStatusIndex = statusOrder.indexOf(editedTask.status);

  // Últimas atualizações para exibir abaixo da caixa de texto
  const recentComments = editedTask.comments
    .filter(c => c.userName !== "Sistema")
    .slice(-2)
    .reverse();

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogOverlay className="bg-black/50" />
        <DialogContent 
          // min-w-[960px] deixava o modal maior que a tela no celular: metade
          // dos controles (inclusive delegar) ficava fora da viewport. O piso
          // de largura passa a valer só a partir de md.
          className={`p-0 border-0 overflow-hidden flex flex-col max-w-none ${
            isFullscreen
              ? 'w-full h-full max-h-none'
              : 'w-[96vw] h-[92vh] max-w-[1280px] md:w-[80vw] md:h-[90vh] md:min-w-[960px]'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* Título/descrição acessíveis: o cabeçalho visual é customizado, mas
              o Radix exige um DialogTitle para leitores de tela. */}
          <DialogTitle className="sr-only">{editedTask.title}</DialogTitle>
          <DialogDescription className="sr-only">
            Detalhes da tarefa, com responsável, status, prazos, atualizações e anexos.
          </DialogDescription>

          {/* Overlay de drag & drop */}
          {isDragging && (
            <div className="fixed inset-0 bg-primary/10 border-2 border-dashed border-primary z-50 flex items-center justify-center">
              <div className="bg-background/95 backdrop-blur p-6 rounded-lg shadow-lg flex flex-col items-center gap-3">
                <FileUp className="w-12 h-12 text-primary" />
                <p className="text-lg font-medium">Solte os arquivos aqui</p>
                <p className="text-sm text-muted-foreground">Os arquivos serão anexados à tarefa</p>
              </div>
            </div>
          )}

          {/* Header - Melhor separação visual */}
          <div className="flex-none bg-card px-6 py-6 border-b border-border/50 shadow-sm sticky top-0 z-10">
            {/* Título editável inline */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1 mr-4">
                {isEditingTitle ? (
                  <Input
                    ref={titleInputRef}
                    value={editedTask.title}
                    onChange={(e) => updateData(prev => ({ ...prev, title: e.target.value }))}
                    onBlur={() => setIsEditingTitle(false)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setIsEditingTitle(false);
                      if (e.key === 'Escape') setIsEditingTitle(false);
                    }}
                    className="text-2xl font-bold border-2 border-primary/20 focus:border-primary bg-background"
                    autoFocus
                  />
                ) : (
                  <div 
                    className="flex items-center gap-2 cursor-pointer group hover:bg-muted/20 p-2 rounded-lg transition-colors"
                    onClick={() => setIsEditingTitle(true)}
                  >
                    <h1 className="text-2xl font-bold text-foreground">{editedTask.title}</h1>
                    <Edit3 className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                )}
              </div>
              
              {/* Actions e Status */}
              <div className="flex items-center gap-3">
                {/* Inline Status */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {state.isSaving && <Loader2 className="w-3 h-3 animate-spin" />}
                  {showSuccessFeedback && (
                    <div className="flex items-center gap-1 text-green-600">
                      <Check className="w-3 h-3" />
                      <span className="text-xs">Salvo</span>
                    </div>
                  )}
                  {getInlineStatus() && !showSuccessFeedback && (
                    <span className={`text-xs ${
                      getInlineStatus()?.type === 'error' ? 'text-destructive' :
                      getInlineStatus()?.type === 'saving' ? 'text-warning' :
                      'text-success'
                    }`}>
                      {getInlineStatus()?.text}
                    </span>
                  )}
                </div>
                
                <Button variant="ghost" size="sm" className="h-11 w-11 shrink-0 p-0 sm:h-9 sm:w-9" onClick={onToggleFullscreen} aria-label="Alternar tela cheia">
                  <Expand className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-11 w-11 shrink-0 p-0 sm:h-9 sm:w-9" onClick={handleModalClose} aria-label="Fechar">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Status e Progress lado a lado */}
            <div className="flex items-center gap-6 mb-4">
              {/* Status */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-muted-foreground">Status:</label>
                <Select value={editedTask.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-auto gap-2 px-4 py-2 border rounded-lg bg-background">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOrder.map((status) => (
                      <SelectItem key={status} value={status}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: getStatusColor(status) }}
                          />
                          <span>{statusLabels[status as keyof typeof statusLabels]}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Progress */}
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-muted-foreground">Progresso:</label>
                <div 
                  className="flex items-center gap-2 cursor-pointer hover:bg-muted/20 p-2 rounded-lg transition-colors"
                  onClick={() => setShowProgressModal(true)}
                >
                  <div className="w-20 h-2 bg-muted-foreground/20 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${getProgressBgColor(calculateProgress())}`}
                      style={{ width: `${calculateProgress()}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${getProgressColor(calculateProgress())}`}>
                    {calculateProgress()}%
                  </span>
                  <Clock className={`w-4 h-4 ${getProgressColor(calculateProgress())}`} />
                </div>
              </div>
            </div>

            {/* Properties Bar */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Responsável — sempre acionável, inclusive quando ninguém está
                  designado (antes o controle vivia dentro do map dos assignees,
                  então tarefa sem responsável não podia ser delegada). */}
              <div className="flex items-center gap-2">
                <Popover open={showAssigneeSelect} onOpenChange={setShowAssigneeSelect}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-auto gap-2 p-1 pr-2 hover:bg-muted/50 rounded-full"
                      aria-label={responsavel ? `Responsável: ${responsavel.name}. Alterar` : "Delegar tarefa"}
                    >
                      {responsavel ? (
                        <>
                          <Avatar className="w-8 h-8 ring-2 ring-primary/20">
                            <AvatarImage src={responsavel.avatar} />
                            <AvatarFallback className="text-xs font-medium">
                              {getIniciais(responsavel.name)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{responsavel.name}</span>
                        </>
                      ) : (
                        <>
                          <span className="flex h-8 w-8 items-center justify-center rounded-full border border-dashed border-muted-foreground/50">
                            <UserPlus className="w-4 h-4 text-muted-foreground" />
                          </span>
                          <span className="text-sm text-muted-foreground">Delegar</span>
                        </>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-2">
                    <div className="space-y-1">
                      <div className="text-sm font-medium mb-2">
                        {responsavel ? "Alterar responsável" : "Delegar para"}
                      </div>
                      <Input
                        placeholder="Buscar membro..."
                        className="mb-2"
                        value={buscaMembro}
                        onChange={(e) => setBuscaMembro(e.target.value)}
                      />
                      <div className="max-h-64 overflow-y-auto space-y-1">
                        {membrosFiltrados.length === 0 ? (
                          <p className="px-2 py-1.5 text-sm text-muted-foreground">
                            {listMembros().length === 0
                              ? "Nenhum membro cadastrado."
                              : `Nenhum membro encontrado para “${buscaMembro}”.`}
                          </p>
                        ) : (
                          membrosFiltrados.map((membro) => (
                            <Button
                              key={membro.id}
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={() => handleAssigneeChange(membro.id)}
                            >
                              <Avatar className="w-6 h-6 mr-2">
                                <AvatarImage src={membro.avatarUrl ?? undefined} />
                                <AvatarFallback className="text-xs">
                                  {getIniciais(membro.nome)}
                                </AvatarFallback>
                              </Avatar>
                              {membro.nome}
                            </Button>
                          ))
                        )}
                      </div>
                      {responsavel && (
                        <Button
                          variant="ghost"
                          className="w-full justify-start text-muted-foreground"
                          onClick={handleAssigneeClear}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Remover responsável
                        </Button>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Datas com indicador de atraso */}
              <Popover open={showStartDatePicker} onOpenChange={setShowStartDatePicker}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-11 sm:h-8">
                    <CalendarIcon className="w-3 h-3 mr-1" />
                    {editedTask.startDate ? formatDate(editedTask.startDate) : 'Início'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={editedTask.startDate}
                    onSelect={(date) => handleDateChange(date, 'start')}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
              
              <Popover open={showEndDatePicker} onOpenChange={setShowEndDatePicker}>
                <PopoverTrigger asChild>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className={cn(
                      "h-11 sm:h-8",
                      editedTask.endDate && isOverdue(editedTask.endDate) && "border-red-200 bg-red-50 text-red-700"
                    )}
                  >
                    <Target className="w-3 h-3 mr-1" />
                    {editedTask.endDate ? formatDate(editedTask.endDate) : 'Prazo'}
                    {editedTask.endDate && isOverdue(editedTask.endDate) && (
                      <AlertCircle className="w-3 h-3 ml-1" />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={editedTask.endDate}
                    onSelect={(date) => handleDateChange(date, 'end')}
                    initialFocus
                    defaultMonth={editedTask.startDate || new Date()}
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>

              {/* Disciplina com cores personalizadas */}
              {showSetorBlock && (
                <div className="relative group">
                  <Popover open={showSetorSelect} onOpenChange={setShowSetorSelect}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 pr-8 sm:h-8">
                        <Badge 
                          variant="outline" 
                          className={cn(
                            "mr-1 border",
                            disciplinaColors[editedTask.setor as keyof typeof disciplinaColors] || disciplinaColors['Geral']
                          )}
                        >
                          {editedTask.setor || 'Geral'}
                        </Badge>
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-2">
                      <div className="space-y-2">
                        <div className="text-sm font-medium mb-2">Setores/Disciplinas</div>
                        {Object.keys(disciplinaColors).map((setor) => (
                          <Button
                            key={setor}
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={() => handleSetorChange(setor)}
                          >
                            <Badge 
                              variant="outline" 
                              className={cn("mr-2 border", disciplinaColors[setor as keyof typeof disciplinaColors])}
                            >
                              {setor}
                            </Badge>
                          </Button>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full p-0 opacity-100 transition-opacity hover:bg-transparent sm:-top-1 sm:-right-1 sm:h-4 sm:w-4 sm:opacity-0 sm:group-hover:opacity-100"
                    onClick={() => setShowSetorBlock(false)}
                  >
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive hover:bg-destructive/80 sm:h-4 sm:w-4">
                      <X className="w-2 h-2 text-destructive-foreground" />
                    </span>
                  </Button>
                </div>
              )}

              {/* Dynamic Tag Selectors */}
              {activeTags.includes("Clientes") && (
                <div className="relative group">
                  <Popover open={showClientSelect} onOpenChange={setShowClientSelect}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 pr-8 sm:h-8">
                        <User className="w-3 h-3 mr-1" />
                        Cliente
                      </Button>
                    </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium mb-2">Selecionar Cliente</div>
                      {["Cliente A", "Cliente B", "Cliente C"].map((cliente) => (
                        <Button
                          key={cliente}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            updateData(prev => ({ ...prev, cliente }));
                            setShowClientSelect(false);
                          }}
                        >
                          {cliente}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full p-0 opacity-100 transition-opacity hover:bg-transparent sm:-top-1 sm:-right-1 sm:h-4 sm:w-4 sm:opacity-0 sm:group-hover:opacity-100"
                  onClick={() => handleRemoveTag("Clientes")}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive hover:bg-destructive/80 sm:h-4 sm:w-4">
                      <X className="w-2 h-2 text-destructive-foreground" />
                    </span>
                </Button>
              </div>
              )}

              {activeTags.includes("Fornecedor") && (
                <div className="relative group">
                  <Popover open={showSupplierSelect} onOpenChange={setShowSupplierSelect}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 pr-8 sm:h-8">
                        <Users className="w-3 h-3 mr-1" />
                        Fornecedor
                      </Button>
                    </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium mb-2">Selecionar Fornecedor</div>
                      {["Fornecedor A", "Fornecedor B", "Fornecedor C"].map((fornecedor) => (
                        <Button
                          key={fornecedor}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            updateData(prev => ({ ...prev, fornecedor }));
                            setShowSupplierSelect(false);
                          }}
                        >
                          {fornecedor}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full p-0 opacity-100 transition-opacity hover:bg-transparent sm:-top-1 sm:-right-1 sm:h-4 sm:w-4 sm:opacity-0 sm:group-hover:opacity-100"
                  onClick={() => handleRemoveTag("Fornecedor")}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive hover:bg-destructive/80 sm:h-4 sm:w-4">
                      <X className="w-2 h-2 text-destructive-foreground" />
                    </span>
                </Button>
              </div>
              )}

              {activeTags.includes("Materiais") && (
                <div className="relative group">
                  <Popover open={showMaterialSelect} onOpenChange={setShowMaterialSelect}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 pr-8 sm:h-8">
                        <Paperclip className="w-3 h-3 mr-1" />
                        Material
                      </Button>
                    </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium mb-2">Selecionar Material</div>
                      {["Cimento", "Areia", "Brita", "Ferro"].map((material) => (
                        <Button
                          key={material}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            updateData(prev => ({ ...prev, material }));
                            setShowMaterialSelect(false);
                          }}
                        >
                          {material}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full p-0 opacity-100 transition-opacity hover:bg-transparent sm:-top-1 sm:-right-1 sm:h-4 sm:w-4 sm:opacity-0 sm:group-hover:opacity-100"
                  onClick={() => handleRemoveTag("Materiais")}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive hover:bg-destructive/80 sm:h-4 sm:w-4">
                      <X className="w-2 h-2 text-destructive-foreground" />
                    </span>
                </Button>
              </div>
              )}

              {activeTags.includes("Equipamentos") && (
                <div className="relative group">
                  <Popover open={showEquipmentSelect} onOpenChange={setShowEquipmentSelect}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 pr-8 sm:h-8">
                        <Activity className="w-3 h-3 mr-1" />
                        Equipamento
                      </Button>
                    </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium mb-2">Selecionar Equipamento</div>
                      {["Betoneira", "Guindaste", "Escavadeira"].map((equipamento) => (
                        <Button
                          key={equipamento}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            updateData(prev => ({ ...prev, equipamento }));
                            setShowEquipmentSelect(false);
                          }}
                        >
                          {equipamento}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full p-0 opacity-100 transition-opacity hover:bg-transparent sm:-top-1 sm:-right-1 sm:h-4 sm:w-4 sm:opacity-0 sm:group-hover:opacity-100"
                  onClick={() => handleRemoveTag("Equipamentos")}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive hover:bg-destructive/80 sm:h-4 sm:w-4">
                      <X className="w-2 h-2 text-destructive-foreground" />
                    </span>
                </Button>
              </div>
              )}

              {activeTags.includes("Projeto/Obra") && (
                <div className="relative group">
                  <Popover open={showProjectSelect} onOpenChange={setShowProjectSelect}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="h-11 pr-8 sm:h-8">
                        <Target className="w-3 h-3 mr-1" />
                        Projeto/Obra
                      </Button>
                    </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium mb-2">Selecionar Projeto/Obra</div>
                      {projects.map((project) => (
                        <Button
                          key={project.id}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => {
                            updateData(prev => ({ ...prev, projectId: project.id }));
                            setShowProjectSelect(false);
                          }}
                        >
                          {project.name}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute -top-4 -right-4 flex h-11 w-11 items-center justify-center rounded-full p-0 opacity-100 transition-opacity hover:bg-transparent sm:-top-1 sm:-right-1 sm:h-4 sm:w-4 sm:opacity-0 sm:group-hover:opacity-100"
                  onClick={() => handleRemoveTag("Projeto/Obra")}
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-destructive hover:bg-destructive/80 sm:h-4 sm:w-4">
                      <X className="w-2 h-2 text-destructive-foreground" />
                    </span>
                </Button>
              </div>
              )}

              {/* Quick Actions */}
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 sm:h-8 sm:w-8" onClick={handleCopyTask}>
                  <Copy className="w-3 h-3" />
                </Button>

                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 sm:h-8 sm:w-8" onClick={handleExportTask}>
                  <Share className="w-3 h-3" />
                </Button>

                <Popover open={showTagsMenu} onOpenChange={setShowTagsMenu}>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-11 w-11 p-0 sm:h-8 sm:w-8">
                      <MoreHorizontal className="w-3 h-3" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2">
                    <div className="space-y-2">
                      <div className="text-sm font-medium mb-2">Adicionar tags</div>
                      {["Clientes", "Fornecedor", "Materiais", "Equipamentos", "Projeto/Obra"].map((tag) => (
                        <Button
                          key={tag}
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={() => handleAddTag(tag)}
                        >
                          <Tag className="w-4 h-4 mr-2" />
                          {tag}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-hidden">
            <div className="h-full flex flex-col">
              {/* Evita altura artificial e rolagem competindo no ancestral */}
              <div className="flex-1 overflow-hidden flex flex-col min-h-0">
                <Tabs defaultValue="updates" className="min-h-0">
                  {/* Tabs fundidas: Updates + Timeline = Atualizações */}
                  <TabsList className="w-full justify-start border-b rounded-none h-12 p-0 bg-transparent">
                    <TabsTrigger value="updates" className="h-11 sm:h-10 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Atualizações ({editedTask.comments.length})
                    </TabsTrigger>
                    <TabsTrigger value="description" className="h-11 sm:h-10 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                      <FileText className="w-4 h-4 mr-2" />
                      Descrição
                    </TabsTrigger>
                    <TabsTrigger value="files" className="h-11 sm:h-10 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                      <Paperclip className="w-4 h-4 mr-2" />
                      Arquivos ({editedTask.attachments.length})
                    </TabsTrigger>
                    <TabsTrigger value="followers" className="h-11 sm:h-10 data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none">
                      <Users className="w-4 h-4 mr-2" />
                      Seguidores ({editedTask.followers.length})
                    </TabsTrigger>
                  </TabsList>

                  {/* Painel Atualizações: rolagem local ao painel */}
                  <TabsContent value="updates" className="p-6 space-y-6 mt-0 overflow-auto min-h-0">
                    {/* Feed unificado de comentários e eventos do sistema */}
                    <div className="space-y-4">
                      {/* Eventos do sistema e comentários misturados por data */}
                      {editedTask.comments
                        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                        .map((comment) => (
                        <div key={comment.id} className="flex gap-3">
                          <Avatar className="w-8 h-8 flex-shrink-0">
                            <AvatarImage src={comment.userAvatar} />
                            <AvatarFallback className="text-xs">
                              {comment.userName === "Sistema" ? "S" : comment.userName.split(' ').map(n => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 space-y-2">
                            <div className={cn(
                              "border rounded-lg p-3 transition-colors",
                              comment.userName === "Sistema" ? "bg-muted/30 border-muted" : "bg-card border-border"
                            )}>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-sm font-medium">
                                  {comment.userName}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatDateTime(comment.createdAt)}
                                </span>
                                {comment.isPrivate && (
                                  <Badge variant="secondary" className="h-5 text-xs">
                                    <EyeOff className="w-3 h-3 mr-1" />
                                    Interno
                                  </Badge>
                                )}
                                {comment.userName === "Sistema" && (
                                  <Badge variant="outline" className="h-5 text-xs">
                                    <Activity className="w-3 h-3 mr-1" />
                                    Sistema
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm">{comment.message}</p>
                              {comment.userName !== "Sistema" && (
                                <div className="flex items-center gap-2 mt-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-6 px-2"
                                    onClick={() => handleReplyToComment(comment.id, comment.userName)}
                                  >
                                    <MessageCircle className="w-3 h-3 mr-1" />
                                    <span className="text-xs">Responder</span>
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Últimas atualizações acima da caixa de texto */}
                    {recentComments.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-medium text-muted-foreground mb-3">Últimas atualizações:</h4>
                        <div className="space-y-2">
                          {recentComments.map((comment) => (
                            <div key={comment.id} className="flex gap-2 p-2 bg-muted/20 rounded text-xs">
                              <Avatar className="w-5 h-5">
                                <AvatarFallback className="text-xs">
                                  {comment.userName.split(' ').map(n => n[0]).join('')}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <span className="font-medium">{comment.userName}:</span>
                                <span className="ml-1 text-muted-foreground">
                                  {comment.message.length > 80 
                                    ? comment.message.substring(0, 80) + "..." 
                                    : comment.message
                                  }
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Campo de nova atualização */}
                    <div className="border-t pt-6 space-y-4 sticky bottom-0 bg-background/95 backdrop-blur z-10">
                      {/* Reply indicator */}
                      {replyingTo && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                          <MessageCircle className="w-3 h-3" />
                          <span>Respondendo a {editedTask.comments.find(c => c.id === replyingTo)?.userName}</span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-4 w-4 p-0 ml-auto"
                            onClick={() => {
                              setReplyingTo(null);
                              setNewComment("");
                            }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Button 
                          variant={isPrivateComment ? "default" : "outline"} 
                          size="sm" 
                          className="h-11 sm:h-9"
                          onClick={() => setIsPrivateComment(!isPrivateComment)}
                        >
                          {isPrivateComment ? <EyeOff className="w-3 h-3 mr-1" /> : <Eye className="w-3 h-3 mr-1" />}
                          {isPrivateComment ? 'Interno' : 'Visível'}
                        </Button>
                        
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="outline" size="sm" className="h-11 sm:h-9">
                              <Bot className="w-3 h-3 mr-1" />
                              IA Escrever
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="max-w-xs">
                            <div className="space-y-1">
                              <p className="font-medium">Exemplos de uso da IA:</p>
                              <ul className="text-xs space-y-1">
                                <li>• "Gerar atualização de progresso"</li>
                                <li>• "Resumo da semana"</li>
                                <li>• "Próximos passos"</li>
                                <li>• "Relatório de status"</li>
                              </ul>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      
                      <div 
                        className={cn(
                          "border-2 border-dashed rounded-lg transition-colors",
                          isDragging ? "border-primary bg-primary/5" : "border-muted-foreground/20"
                        )}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <Textarea
                          ref={commentTextareaRef}
                          value={newComment}
                          onChange={(e) => setNewComment(e.target.value)}
                          placeholder="Adicionar atualização... (Ctrl+Enter para enviar, ou arraste arquivos aqui)"
                          className="min-h-[100px] border-0 focus-visible:ring-0 bg-transparent resize-none"
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="sm" className="h-11 w-11 p-0 sm:h-9 sm:w-9" onClick={handleUploadClick}>
                            <Paperclip className="w-4 h-4" />
                          </Button>
                          <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                            accept="*/*"
                            multiple
                          />
                          <span className="text-xs text-muted-foreground">
                            Ctrl+Enter para enviar
                          </span>
                        </div>
                        <Button onClick={handleAddComment} size="sm" className="h-11 sm:h-9" disabled={!newComment.trim()}>
                          <Send className="w-4 h-4 mr-1" />
                          Enviar
                        </Button>
                      </div>
                    </div>
                  </TabsContent>

                  {/* Painel Descrição: mesma regra para evitar altura fantasma */}
                  <TabsContent value="description" className="p-6 space-y-4 mt-0 overflow-auto min-h-0">
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">Descrição</label>
                        <Textarea
                          value={editedTask.description || ""}
                          onChange={(e) => updateData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Adicione uma descrição detalhada para esta tarefa..."
                          className="min-h-[120px]"
                        />
                        <div className="flex gap-2 mt-2">
                          <Button variant="outline" size="sm" className="h-11 sm:h-9">
                            <Bot className="w-3 h-3 mr-1" />
                            Melhorar com IA
                          </Button>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <label className="text-sm font-medium">Subtarefas</label>
                          <Button variant="outline" size="sm" className="h-11 sm:h-9">
                            <Bot className="w-3 h-3 mr-1" />
                            Gerar com IA
                          </Button>
                        </div>
                        
                        <div className="space-y-2">
                          {editedTask.checklist.map((subtask) => (
                            <div key={subtask.id} className="flex items-center gap-3 p-3 border rounded-lg group hover:bg-muted/50">
                              <Checkbox 
                                checked={subtask.completed}
                                onCheckedChange={() => toggleSubtask(subtask.id)}
                              />
                              <span className={`flex-1 text-sm ${subtask.completed ? 'line-through text-muted-foreground' : ''}`}>
                                {subtask.title}
                              </span>
                              <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <User className="w-3 h-3" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                  <CalendarIcon className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="flex gap-2 mt-3">
                          <Input
                            value={newSubtask}
                            onChange={(e) => setNewSubtask(e.target.value)}
                            placeholder="Adicionar nova subtarefa..."
                            className="flex-1"
                            onKeyPress={(e) => e.key === 'Enter' && handleAddSubtask()}
                          />
                          <Button onClick={handleAddSubtask} size="sm">
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </TabsContent>


                  {/* Painel Seguidores: cabeçalho fixo, lista rolável, sem espaço em branco no topo */}
                  <TabsContent value="followers" className="p-4 space-y-4 mt-0 overflow-auto min-h-0">
                    {/* Cabeçalho fixo no topo do painel */}
                    <div className="sticky top-0 z-10 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70">
                      <div className="flex items-center justify-between py-2">
                        <h3 className="text-sm font-medium">Seguidores da Tarefa</h3>
                        <Popover open={showFollowersSelect} onOpenChange={setShowFollowersSelect}>
                          <PopoverTrigger asChild>
                            <Button
                              className="ml-auto"
                              aria-label="Adicionar seguidor"
                              variant="outline" 
                              size="sm"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              <span className="hidden sm:inline">Adicionar</span>
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 p-2">
                            <div className="space-y-2">
                              <div className="text-sm font-medium mb-2">Adicionar Seguidor</div>
                              {membrosDisponiveis.length === 0 && (
                                <p className="px-2 py-1.5 text-sm text-muted-foreground">
                                  Todos os membros já seguem esta tarefa.
                                </p>
                              )}
                              {membrosDisponiveis
                              .map((user) => (
                                <Button
                                  key={user.id}
                                  variant="ghost"
                                  className="w-full justify-start"
                                  onClick={() => handleAddFollower(user.id)}
                                >
                                  <Avatar className="w-6 h-6 mr-2">
                                    <AvatarImage src={user.avatar} />
                                    <AvatarFallback className="text-xs">
                                      {getIniciais(user.name)}
                                    </AvatarFallback>
                                  </Avatar>
                                  {user.name}
                                </Button>
                              ))}
                              {editedTask.followers.length >= 5 && (
                                <div className="text-xs text-muted-foreground text-center py-2">
                                  Todos os usuários já são seguidores
                                </div>
                              )}
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div className="h-px bg-border" />
                    </div>

                    {/* Conteúdo rolável abaixo do cabeçalho */}
                    {editedTask.followers.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
                        <Users className="h-10 w-10 mb-3" />
                        <div className="font-medium">Nenhum seguidor ainda</div>
                        <div className="text-sm">Adicione pessoas para receberem notificações sobre esta tarefa.</div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {editedTask.followers.map((follower) => (
                          <div key={follower.id} className="flex items-center gap-3 rounded-xl border p-3">
                            <Avatar className="w-10 h-10 shrink-0">
                              <AvatarImage src={follower.avatar} />
                              <AvatarFallback className="text-sm">
                                {follower.name.split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{follower.name}</div>
                              <div className="text-xs text-muted-foreground">Seguindo desde {formatDate(new Date())}</div>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleRemoveFollower(follower.id)}
                              className="text-muted-foreground hover:text-destructive shrink-0"
                              aria-label={`Remover ${follower.name} dos seguidores`}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>

          {/* Progress Update Modal */}
          {showProgressModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-card border rounded-lg p-6 max-w-md w-full mx-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">Atualizar Progresso</h3>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowProgressModal(false)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium">Progresso atual: {calculateProgress()}%</label>
                      <div className="w-full h-2 bg-muted-foreground/20 rounded-full mt-2">
                        <div 
                          className={`h-full rounded-full transition-all duration-300 ${getProgressBgColor(newProgress)}`}
                          style={{ width: `${newProgress}%` }}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium">Novo progresso: {newProgress}%</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={newProgress}
                        onChange={(e) => setNewProgress(Number(e.target.value))}
                        className="w-full mt-2"
                      />
                    </div>
                    
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={newProgress}
                      onChange={(e) => setNewProgress(Number(e.target.value))}
                      placeholder="Digite o percentual"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="outline" 
                      onClick={() => setShowProgressModal(false)}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      onClick={() => {
                        const currentProgress = calculateProgress();
                        const progressChange = newProgress - currentProgress;
                        
                        // Update the task data with new progress
                        updateData(prev => ({
                          ...prev,
                          manualProgress: newProgress, // Store manual progress override
                          updatedAt: new Date(),
                          comments: [...prev.comments, {
                            id: Date.now().toString(),
                            userId: getCurrentUserId() ?? "current-user",
                            userName: getCurrentUserName(),
                            message: `Progresso atualizado de ${currentProgress}% para ${newProgress}% (${progressChange > 0 ? '+' : ''}${progressChange}%)`,
                            createdAt: new Date(),
                            isPrivate: false
                          }]
                        }));
                        
                        // Close modal
                        setShowProgressModal(false);
                      }}
                    >
                      Salvar
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
